import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api, API_BASE } from '../lib/api';
import { AppFrame } from '../components/AppNav';
import { Banner, Field, Loading } from '../components/ui';
import { FieldEditor, fromApiFields, toApiFields } from '../components/FieldEditor';
import type { EditorField } from '../components/FieldEditor';
import { formatExact } from '../lib/format';
import { useSession } from '../state/session';
import { useToast } from '../state/toast';

type Recipient = {
  key: string;
  /** Server id once the draft exists. Sent back on every save so editing a
   *  person's address or position changes them instead of replacing them,
   *  which would cascade away the fields assigned to them. */
  id?: string;
  name: string; email: string; phone: string;
  role: 'signer' | 'copy';
  routing_order: number;
  /** Set when this person came from the document rather than being typed in.
   *  Carries the suggester's key so detected fields can find their owner. */
  detectedKey?: string;
  detectedTitle?: string;
  detectedOrg?: string;
};

let keyCounter = 0;
const newRecipient = (order: number): Recipient => ({
  key: `r${keyCounter++}`, name: '', email: '', phone: '', role: 'signer',
  routing_order: order,
});

// The document comes first and then tells us who signs, rather than asking
// for signatories before anyone has seen the page they appear on.
const STEPS = ['Document', 'Signatories', 'Fields', 'Review'];

export function NewEnvelope() {
  const { orgId, org, user } = useSession();
  const { notify } = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [docs, setDocs] = useState<any[]>([]);
  const [doc, setDoc] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [routing, setRouting] = useState<'sequential' | 'parallel'>('sequential');
  const [authPolicy, setAuthPolicy] = useState('email_otp');
  const [recipients, setRecipients] = useState<Recipient[]>([newRecipient(1)]);
  const [fields, setFields] = useState<EditorField[]>([]);
  const [envelope, setEnvelope] = useState<any>(null);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const attachInput = useRef<HTMLInputElement>(null);
  const [activeAssignee, setActiveAssignee] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [entitlements, setEntitlements] = useState<any>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // What the suggester found, or why it could not. Never collapsed into an
  // empty field list: "it found nothing" and "it could not run" lead the
  // sender to different actions.
  const [detection, setDetection] = useState<any>(null);
  const [detecting, setDetecting] = useState(false);
  const [contacts, setContacts] = useState<any[]>([]);

  useEffect(() => {
    if (!orgId) return;
    api.get(`/organizations/${orgId}/contacts`)
      .then((data) => setContacts(data.contacts || []))
      .catch(() => setContacts([]));   // suggestions are a convenience
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    api.get(`/organizations/${orgId}/documents`).then((data) => {
      setDocs(data.documents);
      if (data.documents.length && !doc) {
        const latest = data.documents[0];
        setDoc(latest);
        setTitle((current) => current || latest.name.replace(/\.pdf$/i, ''));
        api.get(`/organizations/${orgId}/documents/${latest.id}`)
          .then((detail) => setFields(fromApiFields(detail.fields)))
          .catch(() => undefined);
      }
    }).catch(() => undefined);
    api.get(`/organizations/${orgId}/billing`)
      .then((data) => setEntitlements(data.entitlements)).catch(() => undefined);
    if (org) setAuthPolicy(org.auth_policy);
  }, [orgId, org]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadAttachment = useCallback(async (file: File) => {
    if (!orgId) return;
    setBusy(true); setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const uploaded = await api.post(`/organizations/${orgId}/documents`, body);
      setDocs((current) => [uploaded, ...current]);
      setAttachmentIds((current) => [...current, uploaded.id]);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'That attachment could not be uploaded.');
    } finally {
      setBusy(false);
    }
  }, [orgId]);

  const upload = useCallback(async (file: File) => {
    if (!orgId) return;
    setBusy(true); setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const uploaded = await api.post(`/organizations/${orgId}/documents`, body);
      setDoc(uploaded);
      setFields([]);
      setTitle((current) => current || uploaded.name.replace(/\.pdf$/i, ''));
      setDocs((current) => [uploaded, ...current]);
      void runDetection(uploaded);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That file could not be uploaded.');
    } finally {
      setBusy(false);
    }
  }, [orgId]);

  /**
   * Ask the suggester where the fields go and who signs.
   *
   * Everything it returns is a proposal the sender reviews — a signature box
   * in the wrong place is worse than no box at all, so nothing is applied
   * without someone looking at it. A failure is surfaced as a failure rather
   * than as an empty result, because "no fields found" is something a sender
   * would act on.
   */
  const runDetection = useCallback(async (document: any) => {
    if (!orgId || !document) return;
    setDetecting(true);
    setDetection(null);
    try {
      const result = await api.post(
        `/organizations/${orgId}/documents/${document.id}/detect`);
      const found = result.detection;
      setDetection(found);

      // One recipient per detected signatory, in document order, carrying the
      // name we read off the page. The sender supplies only the address.
      if (found?.signatories?.length) {
        setRecipients(found.signatories.map((person: any, index: number) => ({
          ...newRecipient(index + 1),
          name: person.name || '',
          // Carried across from the preparation step when this document was
          // prepared before signing up, so an address is typed once.
          email: person.email || '',
          role: 'signer' as const,
          detectedKey: person.key,
          detectedTitle: person.title || '',
          detectedOrg: person.organization || '',
        })));
      }
    } catch (cause) {
      setDetection({
        unavailable: true,
        reason: cause instanceof ApiError ? cause.message
          : 'Field suggestions are unavailable. Place the fields yourself.',
      });
    } finally {
      setDetecting(false);
    }
  }, [orgId]);

  /** Create or update the draft so recipient ids exist before assigning fields. */
  const syncDraft = useCallback(async () => {
    if (!orgId || !doc) return null;
    const payload = {
      title, message, routing, auth_policy: authPolicy,
      document_id: doc.id,
      attachment_document_ids: attachmentIds,
      recipients: recipients.map((r, index) => ({
        recipient_id: r.id,
        name: r.name, email: r.email, phone: r.phone || undefined,
        role: r.role,
        routing_order: routing === 'parallel' ? 1 : (r.routing_order || index + 1),
      })),
    };
    const result = envelope
      ? await api.patch(`/organizations/${orgId}/envelopes/${envelope.id}`, payload)
      : await api.post(`/organizations/${orgId}/envelopes`, payload);
    setEnvelope(result);
    // Adopt the ids the server assigned, so the next save edits these people.
    setRecipients((current) => current.map((r, index) => ({
      ...r, id: result.recipients[index]?.id ?? r.id,
    })));
    const firstSigner = result.recipients.find((r: any) => r.role === 'signer');
    setActiveAssignee((current) =>
      result.recipients.some((r: any) => r.id === current)
        ? current : firstSigner?.id || null);
    return result;
  }, [orgId, doc, title, message, routing, authPolicy, recipients, envelope, activeAssignee]);

  const goToFields = async () => {
    setBusy(true); setError(null);
    try {
      const result = await syncDraft();
      if (!result) return;
      const signer = result.recipients.find((r: any) => r.role === 'signer');

      // Detected fields become editable fields now, and not before: they need
      // the recipient ids the draft just created. The suggester's signatory
      // key maps to a person through the recipient row it produced, so a
      // field lands on whoever that row has become — including if the sender
      // corrected the name.
      let next = fields;
      if (!fields.length && detection?.fields?.length) {
        const ownerFor = new Map<string, string>();
        recipients.forEach((person, index) => {
          const saved = result.recipients[index];
          if (person.detectedKey && saved?.id) {
            ownerFor.set(person.detectedKey, saved.id);
          }
        });
        next = detection.fields
          .filter((f: any) => f.kind !== 'radio')
          .map((f: any, index: number) => ({
            localId: `d${index}`,
            kind: f.kind,
            label: f.label || f.kind,
            page: f.page,
            x: f.x, y: f.y, width: f.width, height: f.height,
            required: f.required !== false,
            recipient_id: (f.signatory_key && ownerFor.get(f.signatory_key))
              || signer?.id || null,
          }));
      }

      // Re-point any field at a recipient that still exists.
      setFields(next.map((f) => ({
        ...f,
        recipient_id: result.recipients.some((r: any) => r.id === f.recipient_id)
          ? f.recipient_id : signer?.id || null,
      })));
      setStep(2);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not save the draft.');
    } finally {
      setBusy(false);
    }
  };

  const goToReview = async () => {
    if (!orgId || !envelope) return;
    setBusy(true); setError(null);
    try {
      const result = await api.patch(`/organizations/${orgId}/envelopes/${envelope.id}`,
                                     { fields: toApiFields(fields) });
      setEnvelope(result);
      setStep(3);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not save the fields.');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!orgId || !envelope) return;
    setBusy(true); setError(null);
    try {
      const result = await api.post(
        `/organizations/${orgId}/envelopes/${envelope.id}/send`);
      if (result?.dispatch_state === 'unconfirmed') {
        notify('Agreement sent, but we could not confirm the invitation arrived. '
               + 'Open it to record what happened.', 'danger');
      } else if (result?.dispatch_state === 'retrying') {
        notify('Agreement sent, but the invitation has not been delivered yet. '
               + 'We are retrying.', 'danger');
      } else {
        notify('Agreement sent.', 'ok');
      }
      navigate(`/app/envelopes/${envelope.id}`);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not send the agreement.');
      setBusy(false);
    }
  };

  const myEmail = (user?.email || '').trim().toLowerCase();
  const alreadyIncluded = Boolean(myEmail) && recipients.some(
    (r) => r.email.trim().toLowerCase() === myEmail);

  /**
   * Add the signed-in user as a signer.
   *
   * Fills the first blank row rather than appending, because on a fresh form
   * appending would leave an empty recipient above the one you just asked for
   * -- which then fails validation on send.
   */
  const addMyself = () => {
    if (!user || alreadyIncluded) return;
    const blank = recipients.findIndex(
      (r) => !r.name.trim() && !r.email.trim());
    const mine = {
      name: user.name || user.email,
      email: user.email,
      phone: '',
      role: 'signer' as const,
    };
    if (blank >= 0) {
      setRecipients(recipients.map((r, index) =>
        index === blank ? { ...r, ...mine } : r));
    } else {
      setRecipients([...recipients,
                     { ...newRecipient(recipients.length + 1), ...mine }]);
    }
  };

  const signers = (envelope?.recipients || []).filter((r: any) => r.role === 'signer');
  const selfOnly = recipients.length === 1 && recipients[0].role === 'signer'
    && recipients[0].email.trim().toLowerCase() === user?.email;

  return (
    <AppFrame actions={<Link className="btn btn--small btn--ghost" to="/app">Cancel</Link>}>
      <div className="shell section stack">
        <header className="stack-s">
          <h1 style={{ margin: 0 }}>New agreement</h1>
          <ol className="steps">
            {STEPS.map((label, index) => (
              <li key={label} className={`step${index === step ? ' step--active' : ''}${index < step ? ' step--done' : ''}`}>
                <span className="step__num" aria-hidden="true">{index < step ? '✓' : index + 1}</span>
                {label}
                {index < STEPS.length - 1 && <span className="step__sep" aria-hidden="true">›</span>}
              </li>
            ))}
          </ol>
        </header>

        {error && <Banner tone="danger" title="That did not work">{error}</Banner>}

        {step === 0 && (
          <div className="card stack">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Choose a document</h2>
            {docs.length > 0 && (
              <Field label="Use an existing upload" id="doc-pick">
                <select id="doc-pick" className="select" value={doc?.id || ''}
                        onChange={(event) => {
                          const picked = docs.find((d) => d.id === event.target.value);
                          setDoc(picked); setFields([]);
                          // A different document has different signatories;
                          // keeping the old ones would silently send to the
                          // wrong people.
                          setDetection(null);
                          setRecipients([newRecipient(1)]);
                          if (picked) void runDetection(picked);
                        }}>
                  {docs.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.page_count}p)
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Or upload a new PDF" id="doc-upload"
                   hint="Up to 25 MB and 100 pages.">
              <input id="doc-upload" ref={fileInput} type="file" className="input"
                     accept="application/pdf,.pdf" style={{ paddingTop: 10 }}
                     disabled={busy}
                     onChange={(event) => {
                       const file = event.target.files?.[0];
                       if (file) void upload(file);
                     }} />
            </Field>
            {busy && <Loading label="Validating…" />}
            <Field label="Agreement title" id="title" required>
              <input id="title" className="input" value={title}
                     onChange={(event) => setTitle(event.target.value)} />
            </Field>
            <Field label="Message to recipients" id="message">
              <textarea id="message" className="textarea" value={message}
                        onChange={(event) => setMessage(event.target.value)} />
            </Field>

            <div className="panel stack-s">
              <strong style={{ fontSize: '.92rem' }}>Attachments (optional)</strong>
              <p className="small muted" style={{ margin: 0 }}>
                Supporting documents your signer should read. They carry no
                fields and are not signed, but they are frozen with the
                agreement and included in the evidence export.
              </p>
              {attachmentIds.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0,
                             display: 'grid', gap: 6 }}>
                  {attachmentIds.map((id) => {
                    const item = docs.find((d) => d.id === id);
                    return (
                      <li key={id} className="row row--between"
                          style={{ gap: 8, fontSize: '.88rem' }}>
                        <span className="truncate">
                          {item?.name || id}
                          {item && <span className="muted"> · {item.page_count}p</span>}
                        </span>
                        <button type="button" className="btn btn--small btn--ghost"
                                onClick={() => setAttachmentIds(
                                  attachmentIds.filter((x) => x !== id))}>
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <Field label="Add a supporting document" id="attach-upload">
                <input id="attach-upload" ref={attachInput} type="file"
                       className="input" accept="application/pdf,.pdf"
                       style={{ paddingTop: 10 }} disabled={busy}
                       onChange={(event) => {
                         const file = event.target.files?.[0];
                         if (file) void uploadAttachment(file);
                         if (attachInput.current) attachInput.current.value = '';
                       }} />
              </Field>
            </div>
            <div className="btn-row btn-row--stack row--end">
              <button className="btn btn--primary" disabled={!doc || title.trim().length < 2}
                      onClick={() => setStep(1)}>
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="card stack">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Signatories</h2>

            {/* Addresses this organization has sent to before. A datalist
                rather than a custom combobox: the browser's own suggestion UI
                is keyboard- and screen-reader-native, and this is a
                convenience, not a directory. */}
            {contacts.length > 0 && (
              <datalist id="known-contacts">
                {contacts.map((contact: any) => (
                  <option key={contact.email} value={contact.email}>
                    {contact.name ? `${contact.name} — ${contact.email}` : contact.email}
                  </option>
                ))}
              </datalist>
            )}

            {detecting && (
              <Banner tone="info" title="Reading the document">
                Looking for signature blocks and the people named in them.
              </Banner>
            )}

            {/* Each outcome says what it is. Collapsing them into "no fields
                found" would have the sender place nothing and send an
                agreement nobody can sign. */}
            {!detecting && detection?.unavailable && (
              <Banner tone="warn" title="Suggestions unavailable">
                {detection.reason} You can still add signatories and place
                fields yourself — nothing else is affected.
              </Banner>
            )}
            {!detecting && detection?.source === 'no_text_layer' && (
              <Banner tone="warn" title="This document has no readable text">
                {detection.reason}
              </Banner>
            )}
            {!detecting && detection && !detection.unavailable
              && detection.source !== 'no_text_layer'
              && (detection.signatories?.length > 0) && (
              <Banner tone="ok" title={
                detection.signatories.length === 1
                  ? 'Found one signatory in the document'
                  : `Found ${detection.signatories.length} signatories in the document`}>
                Names came from the document. Check them, add an email address
                for each, and remove anyone who should not receive it.
                {detection.notes ? ` ${detection.notes}` : ''}
              </Banner>
            )}
            {!detecting && detection && !detection.unavailable
              && detection.source !== 'no_text_layer'
              && !(detection.signatories?.length > 0) && (
              <Banner tone="info" title="No signature block found">
                We could read the document but could not find a signature
                block in it. Add the signatories yourself below.
              </Banner>
            )}

            {recipients.map((recipient, index) => (
              <div className="panel stack-s" key={recipient.key}>
                <div className="row row--between">
                  <strong style={{ fontSize: '.9rem' }}>
                    {recipient.role === 'copy' ? 'Copy recipient' : `Signer ${index + 1}`}
                    {recipient.detectedKey && (
                      <span className="pill pill--ok" style={{ marginLeft: 8 }}>
                        from the document
                      </span>
                    )}
                  </strong>
                  {recipients.length > 1 && (
                    <button type="button" className="btn btn--small btn--ghost"
                            onClick={() => setRecipients(recipients.filter((r) => r.key !== recipient.key))}>
                      Remove
                    </button>
                  )}
                </div>
                <div className="grid grid--two">
                  <Field label="Name" id={`rn-${recipient.key}`} required>
                    <input id={`rn-${recipient.key}`} className="input" value={recipient.name}
                           onChange={(event) => setRecipients(recipients.map((r) =>
                             r.key === recipient.key ? { ...r, name: event.target.value } : r))} />
                  </Field>
                  <Field label="Email" id={`re-${recipient.key}`} required
                         hint={recipient.detectedKey
                           ? 'The document named this person but not their address.'
                           : undefined}>
                    <input id={`re-${recipient.key}`} className="input" type="email"
                           inputMode="email" value={recipient.email}
                           list={contacts.length ? 'known-contacts' : undefined}
                           onChange={(event) => {
                             const value = event.target.value;
                             // Picking a known address fills the name too, but
                             // never overwrites a name read from the document.
                             const known = contacts.find(
                               (c: any) => c.email === value.trim().toLowerCase());
                             setRecipients(recipients.map((r) => r.key === recipient.key
                               ? { ...r, email: value,
                                   name: (known?.name && !r.name) ? known.name : r.name }
                               : r));
                           }} />
                  </Field>
                </div>
                {(recipient.detectedTitle || recipient.detectedOrg) && (
                  <p className="small muted" style={{ margin: 0 }}>
                    Read from the document:{' '}
                    {[recipient.detectedTitle, recipient.detectedOrg]
                      .filter(Boolean).join(' · ')}
                  </p>
                )}
                {authPolicy === 'sms_otp' && recipient.role === 'signer' && (
                  <Field label="Mobile number" id={`rp-${recipient.key}`} required
                         hint="Required for SMS passcode authentication.">
                    <input id={`rp-${recipient.key}`} className="input" type="tel"
                           inputMode="tel" value={recipient.phone}
                           onChange={(event) => setRecipients(recipients.map((r) =>
                             r.key === recipient.key ? { ...r, phone: event.target.value } : r))} />
                  </Field>
                )}
                <Field label="Role" id={`rr-${recipient.key}`}>
                  <select id={`rr-${recipient.key}`} className="select" value={recipient.role}
                          onChange={(event) => setRecipients(recipients.map((r) =>
                            r.key === recipient.key
                              ? { ...r, role: event.target.value as 'signer' | 'copy' } : r))}>
                    <option value="signer">Needs to sign</option>
                    <option value="copy">Receives a copy only</option>
                  </select>
                </Field>
              </div>
            ))}

            <div className="btn-row">
              <button type="button" className="btn btn--small"
                      disabled={recipients.length >= 10}
                      onClick={() => setRecipients([...recipients, newRecipient(recipients.length + 1)])}>
                Add recipient
              </button>
              {user && (
                <button type="button" className="btn btn--small"
                        disabled={alreadyIncluded || recipients.length >= 10}
                        title={alreadyIncluded
                          ? 'You are already one of the recipients.'
                          : 'Add yourself as a signer'}
                        onClick={addMyself}>
                  {alreadyIncluded ? '✓ You are a signer' : 'Add myself'}
                </button>
              )}
            </div>


            <div className="grid grid--two">
              <Field label="Signing order" id="routing">
                <select id="routing" className="select" value={routing}
                        onChange={(event) => setRouting(event.target.value as any)}>
                  <option value="sequential">One at a time, in order</option>
                  <option value="parallel">Everyone at once</option>
                </select>
              </Field>
              <Field label="How recipients verify their identity" id="auth"
                     hint="An emailed link or email code shows access to that mailbox. It is not verified legal identity.">
                <select id="auth" className="select" value={authPolicy}
                        onChange={(event) => setAuthPolicy(event.target.value)}>
                  <option value="email_link">Personal emailed link only</option>
                  <option value="email_otp">Email one-time passcode</option>
                  {org?.sms_otp_enabled && <option value="sms_otp">SMS one-time passcode</option>}
                </select>
              </Field>
            </div>

            {selfOnly && (
              <Banner tone="ok" title="This is a self-signed agreement">
                You are the only signer, so this does not use a signature request
                from your allowance.
              </Banner>
            )}

            <div className="btn-row btn-row--stack row--end">
              <button className="btn" onClick={() => setStep(0)}>Back</button>
              <button className="btn btn--primary" disabled={busy ||
                        recipients.some((r) => !r.name.trim() || !r.email.trim())}
                      onClick={() => void goToFields()}>
                {busy ? 'Saving…' : 'Continue to fields'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && doc && envelope && detection?.fields?.length > 0 && (
          <Banner tone="info" title="These positions are suggestions">
            Placed from the signature block we read in the document. Drag,
            resize or delete any of them — what you leave here is what signers
            will see.
          </Banner>
        )}

        {step === 2 && doc && envelope && (
          <>
            <FieldEditor
              url={`${API_BASE}/organizations/${orgId}/documents/${doc.id}/file`}
              pageSizes={doc.page_sizes}
              fields={fields}
              onChange={setFields}
              assignees={signers.map((r: any) => ({ id: r.id, name: r.name }))}
              activeAssignee={activeAssignee}
              onActiveAssignee={setActiveAssignee}
            />
            <div className="actionbar">
              <button className="btn" onClick={() => setStep(1)}>Back</button>
              <span className="spacer" />
              <button className="btn btn--primary" disabled={busy || fields.length === 0}
                      onClick={() => void goToReview()}>
                {busy ? 'Saving…' : 'Review and send'}
              </button>
            </div>
          </>
        )}

        {step === 3 && envelope && (
          <div className="stack">
            <div className="card stack">
              <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Review before sending</h2>
              <div className="kv">
                <div className="kv__k">Title</div><div className="kv__v">{envelope.title}</div>
                <div className="kv__k">Document</div>
                <div className="kv__v">{envelope.document.name} ({envelope.document.page_count} pages)</div>
                <div className="kv__k">Signing order</div>
                <div className="kv__v">
                  {envelope.routing === 'sequential' ? 'One at a time, in order' : 'Everyone at once'}
                </div>
                <div className="kv__k">Authentication</div>
                <div className="kv__v">
                  {{ email_link: 'Personal emailed link', email_otp: 'Email one-time passcode',
                     sms_otp: 'SMS one-time passcode' }[envelope.auth_policy as string]}
                </div>
                <div className="kv__k">Recipients</div>
                <div className="kv__v">
                  {envelope.recipients.map((r: any) => (
                    <div key={r.id}>
                      {r.name} — {r.email}{' '}
                      <span className="muted small">
                        ({r.role === 'copy' ? 'copy only' : `signer, step ${r.routing_order}`})
                      </span>
                    </div>
                  ))}
                </div>
                <div className="kv__k">Fields</div>
                <div className="kv__v">{envelope.fields.length} placed</div>
                {envelope.attachments?.length > 0 && (
                  <>
                    <div className="kv__k">Attachments</div>
                    <div className="kv__v">
                      {envelope.attachments.map((a: any) => (
                        <div key={a.document_id}>
                          {a.name} <span className="muted small">
                            ({a.page_count} pages)
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            <Banner tone="warn" title="Sending freezes these terms">
              The document, attachments, recipients and field definitions are frozen
              and hashed at send. Changing any of them afterwards requires a
              replacement agreement, which uses another signature request.
            </Banner>

            {entitlements?.allowance?.state !== 'inactive' && !envelope.self_sign_only && (
              <p className="small muted">
                This uses 1 of your {entitlements?.allowance?.remaining} remaining
                signature requests. The allowance resets{' '}
                {formatExact(entitlements?.allowance?.next_reset_at)}.
              </p>
            )}

            <div className="actionbar">
              <button className="btn" onClick={() => setStep(2)}>Back to fields</button>
              <span className="spacer" />
              <button className="btn btn--primary btn--large" disabled={busy}
                      onClick={() => void send()}>
                {busy ? 'Sending…' : 'Send for signature'}
              </button>
            </div>
          </div>
        )}
      </div>
    </AppFrame>
  );
}
